import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { Page } from '@/components/layout/Page'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'

export { FormError, ListState, tdClass, thClass } from '@/components/admin/AdminShared'

const STATUS_TONE: Record<string, string> = {
  active: 'bg-status-resolved-bg text-status-resolved-fg',
  inactive: 'bg-status-closed-bg text-status-closed-fg',
  retired: 'bg-status-closed-bg text-status-closed-fg',
  suspended: 'bg-status-escalated-bg text-status-escalated-fg',
}

/** Active / Inactive / Retired / Suspended pill (same shape as the SLA admin badges). */
export function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation()
  return (
    <span className={cn('inline-block whitespace-nowrap rounded-sm px-2 py-[3px] text-label font-medium', STATUS_TONE[status] ?? STATUS_TONE.inactive)}>
      {t(`catalog.status.${status}`, { defaultValue: status })}
    </span>
  )
}

export function ValidatedBadge({ validated }: { validated: boolean }) {
  const { t } = useTranslation()
  return (
    <span
      className={cn(
        'inline-block whitespace-nowrap rounded-sm px-2 py-[3px] text-label font-medium',
        validated ? 'bg-status-progress-bg text-status-progress-fg' : 'bg-status-new-bg text-status-new-fg',
      )}
    >
      {validated ? t('catalog.validated') : t('catalog.notValidated')}
    </span>
  )
}

/** Underlined tab bar, as on the SLA admin page. */
export function TabBar<T extends string>({ tabs, value, onChange, labelKey }: { tabs: readonly T[]; value: T; onChange: (t: T) => void; labelKey: string }) {
  const { t } = useTranslation()
  return (
    <div role="tablist" className="flex gap-6 border-b border-line">
      {tabs.map((x) => (
        <button
          key={x}
          type="button"
          role="tab"
          aria-selected={x === value}
          onClick={() => onChange(x)}
          className={cn(
            '-mb-px border-b-2 pb-2.5 text-body',
            x === value ? 'border-ink font-semibold text-ink-strong' : 'border-transparent text-ink-muted hover:text-ink',
          )}
        >
          {t(`${labelKey}.${x}`)}
        </button>
      ))}
    </div>
  )
}

export function BackLink({ to }: { to: string }) {
  const { t } = useTranslation()
  return (
    <Link to={to} className="inline-flex w-fit items-center gap-1 text-meta font-medium text-ink-muted hover:text-ink">
      <ChevronLeft className="size-4" />
      {t('catalog.back')}
    </Link>
  )
}

/** The catalog pages are superadmin-only (the API answers 403 to everyone else). */
export function SuperAdminGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  if (!user?.isSuperAdmin)
    return (
      <Page>
        <p className="text-body text-ink-muted">{t('catalog.superAdminOnly')}</p>
      </Page>
    )
  return <>{children}</>
}

/** Small text action used in table rows. */
export function TextAction({ onClick, children, danger, disabled }: { onClick: () => void; children: ReactNode; danger?: boolean; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'text-meta font-medium hover:underline disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:no-underline',
        danger ? 'text-status-escalated-fg' : 'text-ink',
      )}
    >
      {children}
    </button>
  )
}
